import { useAuth } from 'react-oidc-context';

declare module 'react-oidc-context' {
	interface AuthContextProps {
		/** @deprecated Perfer `/modules/oidc/useAuthenticated` */
		isAuthenticated: boolean;
	}
}

// Add LW specific fields
declare module 'oidc-client-ts' {
	interface IdTokenClaims {
		role?: string[];
		group?: string[];
		masquerade?: boolean;
	}
}

/**
 * Checks if the logged in user is a valid token including the required `access_token` for the api
 */
export function useAuthenticated(): boolean {
	const { isAuthenticated, user } = useAuth();

	return isAuthenticated && Boolean(user?.access_token);
}

/**
 * isSetupUser
 */
export function useSetupUser(): boolean {
	const isAuthenticated = useAuthenticated();
	const { user } = useAuth();
	const { role = [] } = user?.profile || {};

	return isAuthenticated && role.includes('SetupUser');
}

/**
 * isKeroOnly
 */
export function useKeroOnly(): boolean {
	const isAuthenticated = useAuthenticated();
	const { user } = useAuth();
	const { role = [], group = [] } = user?.profile || {};

	const hasRole = role.length > 0;
	const hasGroup = group.length > 0;

	return isAuthenticated && !hasRole && !hasGroup;
}

/**
 * isBasketAdmin
 */
export function useBasketAdmin(): boolean {
	const isAuthenticated = useAuthenticated();
	const { user } = useAuth();
	const { group = [] } = user?.profile || {};
	const hasBasketGroup =
		group instanceof Array && group.includes('basket-admin');

	return isAuthenticated && hasBasketGroup;
}

/**
 * isMasquerade
 */
export function useMasquerade(): boolean {
	const isAuthenticated = useAuthenticated();
	const { user } = useAuth();
	const scopes = (user?.scope || '').split(' ');
	const isMasquerade =
		scopes.includes('masquerade') || Boolean(user?.profile?.masquerade);

	return isAuthenticated && isMasquerade;
}

export const useUsername = () => {
	const { user } = useAuth();
	const username = user?.profile?.username || '';

	if (typeof username === 'string') {
		return username;
	}

	return undefined;
};

/**
 * User is self
 */
export const useIsSelf = (username: string): boolean => {
	const currentUsername = useUsername();
	return currentUsername === username;
};

/**
 * User has "AccountLogin" role
 */
export function useAccountLogin(): boolean {
	const isAuthenticated = useAuthenticated();
	const { user } = useAuth();
	const { role = [] } = user?.profile || {};

	return isAuthenticated && role.includes('AccountLogin');
}

/**
 * User has "SuspendedOwner" role
 */
export function useSuspended(): boolean {
	const isAuthenticated = useAuthenticated();
	const { user } = useAuth();
	const { role = [] } = user?.profile || {};

	return isAuthenticated && role.includes('SuspendedOwner');
}

export default useAuthenticated;
